import { useContext, useEffect, useRef, useState } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";
import UnsavedChangesDialog from "../components/UnsavedChangesDialog/UnsavedChangesDialog";

export default function useUnsavedChanges(when, options = {}) {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const [pending, setPending] = useState(null);
  const [saveState, setSaveState] = useState("idle");
  const dirty = useRef(when);
  const pendingRef = useRef(null);
  const saving = useRef(false);
  const saveConfirmed = useRef(false);
  const unblock = useRef(null);
  const mounted = useRef(true);
  dirty.current = when;
  const shouldBlock = when || Boolean(pending);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!shouldBlock) return undefined;
    const stop = navigator.block((transition) => {
      // Keep the original destination while its confirmation/save is pending.
      if (pendingRef.current) return;
      if (!dirty.current) {
        stop();
        transition.retry();
        return;
      }
      pendingRef.current = transition;
      setSaveState("idle");
      setPending(transition);
    });
    unblock.current = stop;
    return () => {
      if (unblock.current === stop) {
        stop();
        unblock.current = null;
      }
    };
  }, [navigator, shouldBlock]);

  const release = () => {
    dirty.current = false;
    unblock.current?.();
    unblock.current = null;
    pendingRef.current = null;
    setPending(null);
  };

  const markSaved = () => {
    // The editor confirms persistence; the guard owns final navigation.
    if (saving.current) {
      saveConfirmed.current = true;
      return;
    }
    release();
  };

  const keepEditing = () => {
    if (saving.current || options.isSaving) return;
    pendingRef.current = null;
    setPending(null);
    setSaveState("idle");
  };

  const discard = () => {
    if (saving.current || options.isSaving) return;
    const transition = pendingRef.current;
    release();
    transition?.retry();
  };

  const saveAndLeave = async () => {
    if (saving.current || options.isSaving || !pendingRef.current || !options.onSave) return;
    saving.current = true;
    saveConfirmed.current = false;
    setSaveState("saving");
    let succeeded = false;
    try {
      const result = await options.onSave();
      succeeded = result === true || saveConfirmed.current;
    } catch {
      // Keep edits and the original destination available for retry.
    }
    saving.current = false;
    if (!mounted.current) return;
    if (succeeded) {
      const transition = pendingRef.current;
      release();
      transition?.retry();
    } else {
      setSaveState("error");
    }
  };

  const dialog = (
    <UnsavedChangesDialog
      open={Boolean(pending)}
      documentName={options.documentName}
      saveMode={options.saveMode}
      isExistingDraft={options.isExistingDraft}
      canSaveDraft={Boolean(options.onSave) && options.canSaveDraft !== false}
      saveState={options.isSaving ? "saving" : saveState}
      onSave={saveAndLeave}
      onKeepEditing={keepEditing}
      onDiscard={discard}
    />
  );

  return { dialog, markSaved };
}
